import * as d3Scale from 'd3-scale'

import {getBranchLocatorPart} from '../../../../rest/locators'
import type {Branch, TestOccurrence} from '../../../../services/rest'
import {stringifyId} from '../../../../types'
import type {BuildTypeId, ProjectId, TestId} from '../../../../types'
import {stringifyBranch} from '../../../../utils/branchNames'
import {getColorHexByString} from '../../../../utils/colorByString'
import cssVariables from '../../../../utils/cssVariables'
import {parseDateToObj} from '../../../../utils/dateTimeUtils'
import {notNull} from '../../../../utils/guards'
import type {WritableKeyValue} from '../../../../utils/object'
import {resolveRelative} from '../../../../utils/url'
import {TestStatus} from '../Tests.types'

import {DataTypes} from './TestHistoryChart.types'
import type {Filter} from './TestHistoryChart.types'

const HISTORY_TESTS_LIMIT = 1000
export const CHART_PADDING = {
  top: 8,
  right: 0,
  bottom: 24,
  left: 64,
}
export const CHART_HEIGHT = 176
const MIN_BAR_WIDTH = 8
const DEFAULT_BAR_WIDTH = 12
export const BAR_MARGIN = 2
export const DEFAULT_BAR_WIDTH_WITH_MARGIN: number = DEFAULT_BAR_WIDTH + BAR_MARGIN
export const MIN_BAR_WIDTH_WITH_MARGIN: number = MIN_BAR_WIDTH + BAR_MARGIN
export const getTestRunsStatistic = (
  testOccurrences: ReadonlyArray<TestOccurrence>,
): {
  success: number
  failure: number
  ignored: number
} =>
  testOccurrences.reduce(
    (acc, testOccurrence) => ({
      success: testOccurrence.status === TestStatus.SUCCESS ? acc.success + 1 : acc.success,
      failure: testOccurrence.status === TestStatus.FAILURE ? acc.failure + 1 : acc.failure,
      ignored: testOccurrence.status === TestStatus.UNKNOWN ? acc.ignored + 1 : acc.ignored,
    }),
    {
      success: 0,
      failure: 0,
      ignored: 0,
    },
  )
export const getLocator = (
  testId: TestId | null | undefined,
  projectId: ProjectId | null | undefined,
  buildTypeId: BuildTypeId | null | undefined,
  branch: Branch | null | undefined,
  count: number = HISTORY_TESTS_LIMIT,
): string => {
  let baseLocator = `affectedProject:${stringifyId(projectId)}`

  if (buildTypeId != null) {
    baseLocator = `buildType:${stringifyId(buildTypeId)}`
  }

  const testLocator = `,test:(id:${stringifyId(testId)}),includePersonal:true`
  const countLocator = `,count:${count}`
  const branchPart = getBranchLocatorPart(branch)
  const branchLocator = branch != null && branchPart != null ? `,branch(${branchPart})` : ''
  return `${baseLocator}${testLocator}${countLocator}${branchLocator}`
}

const getFilteredTestOccurrences = (
  testOccurrences: ReadonlyArray<TestOccurrence>,
  {agents, showFailed, showPersonalBuilds}: Filter,
) =>
  [...testOccurrences].reverse().filter(testOccurrence => {
    const agentName = testOccurrence.build?.agent?.name
    const checkAgent = agents.find(agent => agentName === agent.key) != null
    const checkShowFailed = showFailed || testOccurrence.status !== TestStatus.FAILURE
    const checkShowPersonalBuilds = showPersonalBuilds || testOccurrence.build?.personal === false
    return checkAgent && checkShowFailed && checkShowPersonalBuilds
  })

export const getData = (testOccurrences: ReadonlyArray<TestOccurrence>, filter: Filter) =>
  getFilteredTestOccurrences(testOccurrences, filter).map((testOccurrence, index) => {
    const {id: testOccurrenceId, duration, status, build} = testOccurrence
    const {id: buildId, startDate, agent} = build ?? {}
    return {
      type: DataTypes.DEFAULT as const,
      x: index,
      y: duration ?? 0,
      buildId,
      testOccurrenceId,
      status,
      date: startDate,
      color: getColorHexByString(agent?.name),
    }
  })
export const getAverageData = (
  testOccurrences: ReadonlyArray<TestOccurrence>,
  filter: Filter,
  isAutodetectTimeZone?: boolean | null | undefined,
) => {
  const testOccurrencesByDate: WritableKeyValue<string, TestOccurrence[]> = {}
  const filteredTestOccurrences = getFilteredTestOccurrences(testOccurrences, filter)
  filteredTestOccurrences.forEach(testOccurrence => {
    const date =
      testOccurrence.build?.startDate != null
        ? parseDateToObj(testOccurrence.build.startDate, isAutodetectTimeZone).format('DD MMM YYYY')
        : null

    if (date != null) {
      const occurrences = testOccurrencesByDate[date]
      if (occurrences != null) {
        occurrences.push(testOccurrence)
      } else {
        testOccurrencesByDate[date] = [testOccurrence]
      }
    }
  })
  return Object.entries(testOccurrencesByDate)
    .map(([date, occurrences], index) => {
      if (occurrences == null) {
        return null
      }
      const countBuilds = occurrences.length
      const overallDuraton = occurrences.reduce((acc, item) => acc + (item.duration ?? 0), 0)
      return {
        type: DataTypes.AVERAGE as const,
        date,
        x: index,
        y: Math.round(overallDuraton / countBuilds),
        color: cssVariables['--ring-main-color'] ?? getColorHexByString('average'),
        countBuilds,
      }
    })
    .filter(notNull)
}
const MIN_TOLERANCE = 0.1
const MAX_TOLERANCE = 5
const MULTIPLICITY = 1000
export const getSimplifyTolerance = (count: number): number => {
  const tolerance = Math.round(count / MULTIPLICITY)

  if (tolerance <= MAX_TOLERANCE && tolerance >= MIN_TOLERANCE) {
    return tolerance
  } else if (tolerance > MAX_TOLERANCE) {
    return MAX_TOLERANCE
  }

  return MIN_TOLERANCE
}
export const getDownloadLink = (
  projectId: ProjectId | null | undefined,
  buildTypeId: BuildTypeId | null | undefined,
  testId: TestId | null | undefined,
  branch: Branch | null | undefined,
): string => {
  const stringifiedBranch = stringifyBranch(branch)
  const basePart =
    '/exportchart.html?type=text&@f_range=MONTH&@filter.status=ERROR&@filter.average=false&@filter.personal=true&flotChart=false&_graphKey=g&valueType=TestDuration&id=TestDuration&showBuildType=true&showBranches=true'
  return resolveRelative(
    [
      basePart,
      buildTypeId != null ? `buildTypeId=${stringifyId(buildTypeId)}` : null,
      projectId != null ? `projectId=${stringifyId(projectId)}` : null,
      testId != null ? `testNameId=${stringifyId(testId)}` : null,
      stringifiedBranch != null && stringifiedBranch !== ''
        ? `branchName=${stringifiedBranch}`
        : null,
    ]
      .filter(Boolean)
      .join('&'),
  )
}
export const getAxisTicks = (maxDuration: number, count: number) => {
  const result = []

  if (maxDuration === 0) {
    for (let i = 0; i <= count; i++) {
      // eslint-disable-next-line @typescript-eslint/no-magic-numbers
      result.push(i * 200)
    }
  } else {
    result.push(...d3Scale.scaleLinear().domain([0, maxDuration]).nice(count).ticks(count))
  }

  return result
}
